/**
 * V199: 동적 모달 스택 관리자
 *
 * 문제: Dialog(z-200)와 Sheet(z-250)의 고정 z-index로 인해
 *       중첩 모달에서 순서 문제 발생
 *
 * 해결: 가장 나중에 열린 모달이 항상 최상위에 표시되도록
 *       동적으로 z-index를 관리
 *
 * 사용법:
 * 1. App.tsx에서 ModalStackProvider로 감싸기
 * 2. Dialog/Sheet에서 useModalStack() 훅 사용
 */

import * as React from "react";

// 기본 z-index 설정
const BASE_Z_INDEX = 200;
const Z_INDEX_INCREMENT = 10;
const MAX_Z_INDEX = 500;

interface ModalEntry {
  id: string;
  zIndex: number;
  timestamp: number;
}

interface ModalStackContextValue {
  /**
   * 모달을 스택에 등록하고 z-index 반환
   * @param id 고유 모달 ID (useId()로 생성 권장)
   * @returns 할당된 z-index
   */
  register: (id: string) => number;

  /**
   * 모달을 스택에서 제거
   * @param id 제거할 모달 ID
   */
  unregister: (id: string) => void;

  /**
   * 모달의 현재 z-index 조회
   * @param id 조회할 모달 ID
   * @returns z-index (등록되지 않은 경우 BASE_Z_INDEX)
   */
  getZIndex: (id: string) => number;

  /**
   * 기존 모달을 최상위로 이동
   * @param id 최상위로 이동할 모달 ID
   */
  bringToFront: (id: string) => void;
}

const ModalStackContext = React.createContext<ModalStackContextValue | null>(null);

interface ModalStackProviderProps {
  children: React.ReactNode;
}

export function ModalStackProvider({ children }: ModalStackProviderProps) {
  // 모달 스택 상태
  const [stack, setStack] = React.useState<ModalEntry[]>([]);

  // 다음 z-index 계산
  const getNextZIndex = React.useCallback((currentStack: ModalEntry[]): number => {
    if (currentStack.length === 0) {
      return BASE_Z_INDEX;
    }
    const maxZ = Math.max(...currentStack.map((entry) => entry.zIndex));
    const nextZ = maxZ + Z_INDEX_INCREMENT;
    // 최대값 초과 시 재정렬
    if (nextZ > MAX_Z_INDEX) {
      return BASE_Z_INDEX + (currentStack.length * Z_INDEX_INCREMENT);
    }
    return nextZ;
  }, []);

  // 모달 등록
  const register = React.useCallback((id: string): number => {
    let assignedZIndex = BASE_Z_INDEX;

    setStack((prev) => {
      // 이미 등록된 경우 기존 z-index 반환
      const existing = prev.find((entry) => entry.id === id);
      if (existing) {
        assignedZIndex = existing.zIndex;
        return prev;
      }

      // 새 z-index 할당
      assignedZIndex = getNextZIndex(prev);
      const newEntry: ModalEntry = {
        id,
        zIndex: assignedZIndex,
        timestamp: Date.now(),
      };

      return [...prev, newEntry];
    });

    return assignedZIndex;
  }, [getNextZIndex]);

  // 모달 제거
  const unregister = React.useCallback((id: string): void => {
    setStack((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  // z-index 조회
  const getZIndex = React.useCallback((id: string): number => {
    const entry = stack.find((e) => e.id === id);
    return entry?.zIndex ?? BASE_Z_INDEX;
  }, [stack]);

  // 최상위로 이동
  const bringToFront = React.useCallback((id: string): void => {
    setStack((prev) => {
      const entry = prev.find((e) => e.id === id);
      if (!entry) return prev;

      const nextZ = getNextZIndex(prev);
      return prev.map((e) =>
        e.id === id ? { ...e, zIndex: nextZ, timestamp: Date.now() } : e
      );
    });
  }, [getNextZIndex]);

  const value = React.useMemo<ModalStackContextValue>(() => ({
    register,
    unregister,
    getZIndex,
    bringToFront,
  }), [register, unregister, getZIndex, bringToFront]);

  return (
    <ModalStackContext.Provider value={value}>
      {children}
    </ModalStackContext.Provider>
  );
}

/**
 * 모달 스택 관리 훅
 *
 * @example
 * ```tsx
 * function MyDialog({ open, onOpenChange }) {
 *   const { zIndex, isRegistered } = useModalStack(open);
 *
 *   return (
 *     <DialogPrimitive.Content style={{ zIndex }}>
 *       ...
 *     </DialogPrimitive.Content>
 *   );
 * }
 * ```
 */
export function useModalStack(isOpen: boolean) {
  const context = React.useContext(ModalStackContext);
  const modalId = React.useId();
  const [zIndex, setZIndex] = React.useState(BASE_Z_INDEX);

  // V200: context를 ref로 안정화하여 불필요한 re-render 방지
  const contextRef = React.useRef(context);
  contextRef.current = context;

  React.useEffect(() => {
    // Provider 없이 사용 시 fallback (기존 동작 유지)
    const ctx = contextRef.current;
    if (!ctx) {
      return;
    }

    if (isOpen) {
      const newZIndex = ctx.register(modalId);
      setZIndex(newZIndex);
    } else {
      ctx.unregister(modalId);
    }

    return () => {
      if (isOpen) {
        ctx.unregister(modalId);
      }
    };
  }, [isOpen, modalId]); // context 의존성 제거

  return {
    zIndex,
    isRegistered: context !== null,
    bringToFront: () => context?.bringToFront(modalId),
  };
}

/**
 * Context 직접 접근 훅 (고급 사용)
 */
export function useModalStackContext() {
  const context = React.useContext(ModalStackContext);
  if (!context) {
    throw new Error("useModalStackContext must be used within ModalStackProvider");
  }
  return context;
}
